import { fonts } from '@/constants/fonts';
import { useThemeColor } from '@/hooks/useThemeColor';
import React, { ReactNode } from 'react';
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import ThemedText from '../core/ThemedText';

export interface Tab {
  key: string;
  label: string;
  icon?: ReactNode;
  badge?: number | string;
  disabled?: boolean;
}

type TabVariant = 'line' | 'pill' | 'contained';

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabKey: string) => void;
  variant?: TabVariant;
  scrollable?: boolean;
  style?: ViewStyle;
}

export default function Tabs({
  tabs,
  activeTab,
  onTabChange,
  variant = 'line',
  scrollable = false,
  style,
}: TabsProps) {
  const primaryColor = useThemeColor({}, 'primary');
  const textColor = useThemeColor({}, 'text');
  const textSecondary = useThemeColor({}, 'textSecondary');
  const borderColor = useThemeColor({}, 'border');
  const backgroundSecondary = useThemeColor({}, 'backgroundSecondary');

  const handleTabPress = (tabKey: string, disabled?: boolean) => {
    if (disabled) return;
    onTabChange(tabKey);
  };

  const renderTab = (tab: Tab, index: number) => {
    const isActive = tab.key === activeTab;

    const tabStyle: ViewStyle = {
      ...getTabStyle(variant, isActive, primaryColor, backgroundSecondary),
    };

    const textStyle = {
      color: isActive ? (variant === 'line' ? primaryColor : '#FFFFFF') : textSecondary,
      fontFamily: isActive ? fonts.semiBold : fonts.regular,
    };

    return (
      <TouchableOpacity
        key={tab.key}
        style={[styles.tab, tabStyle, tab.disabled && styles.disabled]}
        onPress={() => handleTabPress(tab.key, tab.disabled)}
        disabled={tab.disabled}
        activeOpacity={0.7}
      >
        {tab.icon && <View style={styles.icon}>{tab.icon}</View>}

        <ThemedText style={[styles.label, textStyle]}>{tab.label}</ThemedText>

        {tab.badge !== undefined && (
          <View
            style={[
              styles.badge,
              {
                backgroundColor: isActive && variant !== 'line' ? '#FFFFFF' : primaryColor,
              },
            ]}
          >
            <ThemedText
              style={[
                styles.badgeText,
                {
                  color: isActive && variant !== 'line' ? primaryColor : '#FFFFFF',
                },
              ]}
            >
              {tab.badge}
            </ThemedText>
          </View>
        )}

        {/* Bottom indicator for 'line' variant */}
        {variant === 'line' && isActive && (
          <View
            style={[
              styles.indicator,
              {
                backgroundColor: primaryColor,
              },
            ]}
          />
        )}
      </TouchableOpacity>
    );
  };

  const Container = scrollable ? ScrollView : View;
  const containerProps = scrollable
    ? {
        horizontal: true,
        showsHorizontalScrollIndicator: false,
        contentContainerStyle: styles.scrollContent,
      }
    : {};

  const containerStyle: ViewStyle = {
    ...getContainerStyle(variant, borderColor),
  };

  return (
    <Container style={[styles.container, containerStyle, style]} {...containerProps}>
      {tabs.map((tab, index) => renderTab(tab, index))}
    </Container>
  );
}

function getContainerStyle(variant: TabVariant, borderColor: string): ViewStyle {
  switch (variant) {
    case 'line':
      return {
        borderBottomWidth: 1,
        borderBottomColor: borderColor,
        flexDirection: 'row',
      };
    case 'pill':
    case 'contained':
      return {
        flexDirection: 'row',
        gap: 8,
      };
  }
}

function getTabStyle(
  variant: TabVariant,
  isActive: boolean,
  primaryColor: string,
  backgroundSecondary: string
): ViewStyle {
  switch (variant) {
    case 'line':
      return {
        paddingVertical: 12,
        paddingHorizontal: 16,
      };
    case 'pill':
      return {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 20,
        backgroundColor: isActive ? primaryColor : backgroundSecondary,
      };
    case 'contained':
      return {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        backgroundColor: isActive ? primaryColor : backgroundSecondary,
      };
  }
}

const styles = StyleSheet.create({
  container: {},
  scrollContent: {
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  icon: {
    marginRight: 8,
  },
  label: {
    fontSize: 14,
  },
  badge: {
    marginLeft: 8,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 11,
    fontFamily: fonts.bold,
  },
  indicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
  },
  disabled: {
    opacity: 0.5,
  },
});
